import { useState, useEffect, useCallback } from 'react';
import { useCanvas } from '../../contexts/CanvasContext';
import { useDesignerStore } from '../../stores';
import { useHistoryStore } from '../../stores/historyStore';
import { t } from '../../i18n';
import styles from './PropertiesPanel.module.css';

// Import fabric for side effects
import 'fabric';

interface FilterState {
  grayscale: boolean;
  sepia: boolean;
  invert: boolean;
  brightness: number;
  contrast: number;
  blur: number;
}

const DEFAULT_FILTERS: FilterState = {
  grayscale: false,
  sepia: false,
  invert: false,
  brightness: 0,
  contrast: 0,
  blur: 0,
};

export const ImageFiltersPanel = () => {
  const { canvas } = useCanvas();
  const { activeObject } = useDesignerStore();
  const { saveSnapshot } = useHistoryStore();
  const [filters, setFilters] = useState<FilterState>(DEFAULT_FILTERS);

  // Check if active object is an image
  const isImageObject = activeObject &&
    activeObject.type === 'image' &&
    !activeObject.isBaseImage &&
    !activeObject.isMaskOverlay;

  // Update local state when selection changes
  useEffect(() => {
    if (!isImageObject || !activeObject) {
      setFilters(DEFAULT_FILTERS);
      return;
    }

    const img = activeObject as fabric.Image;
    const imgFilters = img.filters || [];

    const newFilters: FilterState = { ...DEFAULT_FILTERS };

    imgFilters.forEach((filter: fabric.IBaseFilter) => {
      if (!filter) return;
      const filterType = (filter as unknown as { type: string }).type;

      switch (filterType) {
        case 'Grayscale':
          newFilters.grayscale = true;
          break;
        case 'Sepia':
          newFilters.sepia = true;
          break;
        case 'Invert':
          newFilters.invert = true;
          break;
        case 'Brightness':
          newFilters.brightness = ((filter as unknown as { brightness: number }).brightness || 0) * 100;
          break;
        case 'Contrast':
          newFilters.contrast = ((filter as unknown as { contrast: number }).contrast || 0) * 100;
          break;
        case 'Blur':
          newFilters.blur = (filter as unknown as { blur: number }).blur || 0;
          break;
      }
    });

    setFilters(newFilters);
  }, [activeObject, isImageObject]);

  const saveToHistory = useCallback(() => {
    if (!canvas) return;
    const state = JSON.stringify(canvas.toJSON(['id', 'isGuide', 'isMockup', 'isBaseImage', 'isMaskOverlay']));
    saveSnapshot(state);
  }, [canvas, saveSnapshot]);

  const applyFilters = useCallback((newFilters: FilterState) => {
    if (!canvas || !activeObject || !isImageObject) return;

    const f = window.fabric;
    const img = activeObject as fabric.Image;
    const filterArray: fabric.IBaseFilter[] = [];

    if (newFilters.grayscale) {
      filterArray.push(new f.Image.filters.Grayscale());
    }
    if (newFilters.sepia) {
      filterArray.push(new f.Image.filters.Sepia());
    }
    if (newFilters.invert) {
      filterArray.push(new f.Image.filters.Invert());
    }
    if (newFilters.brightness !== 0) {
      filterArray.push(new f.Image.filters.Brightness({ brightness: newFilters.brightness / 100 }));
    }
    if (newFilters.contrast !== 0) {
      filterArray.push(new f.Image.filters.Contrast({ contrast: newFilters.contrast / 100 }));
    }
    if (newFilters.blur > 0) {
      filterArray.push(new f.Image.filters.Blur({ blur: newFilters.blur }));
    }

    // Preserve laser filter if it exists
    const laserFilter = img.filters?.find((f: any) => f.isLaserFilter);

    img.filters = filterArray;

    if (laserFilter) {
      img.filters.push(laserFilter);
    }

    img.applyFilters();
    canvas.renderAll();
  }, [canvas, activeObject, isImageObject]);

  const handleToggleFilter = (filterName: 'grayscale' | 'sepia' | 'invert') => {
    const newFilters = { ...filters, [filterName]: !filters[filterName] };
    setFilters(newFilters);
    applyFilters(newFilters);
    saveToHistory();
  };

  const handleSliderChange = (filterName: 'brightness' | 'contrast' | 'blur', value: number) => {
    const newFilters = { ...filters, [filterName]: value };
    setFilters(newFilters);
    applyFilters(newFilters);
  };

  const handleSliderEnd = () => {
    saveToHistory();
  };

  const handleResetFilters = () => {
    setFilters(DEFAULT_FILTERS);
    applyFilters(DEFAULT_FILTERS);
    saveToHistory();
  };

  if (!isImageObject) {
    return null;
  }

  return (
    <div className={styles.panel}>
      <div className={styles.panelTitle}>{t('design.filters.title', 'Image Filters')}</div>

      <div className={styles.propertyGroup}>
        <label className={styles.label}>{t('design.filters.effects', 'Effects')}</label>
        <div className={styles.buttonGroup}>
          <button
            className={`${styles.styleButton} ${filters.grayscale ? styles.active : ''}`}
            onClick={() => handleToggleFilter('grayscale')}
            title={t('design.filters.grayscale', 'Grayscale')}
          >
            B/W
          </button>
          <button
            className={`${styles.styleButton} ${filters.sepia ? styles.active : ''}`}
            onClick={() => handleToggleFilter('sepia')}
            title={t('design.filters.sepia', 'Sepia')}
          >
            Sep
          </button>
          <button
            className={`${styles.styleButton} ${filters.invert ? styles.active : ''}`}
            onClick={() => handleToggleFilter('invert')}
            title={t('design.filters.invert', 'Invert Colors')}
          >
            Inv
          </button>
        </div>
      </div>

      <div className={styles.propertyGroup}>
        <label className={styles.label}>{t('design.filters.brightness', 'Brightness')}</label>
        <div className={styles.sliderWrapper}>
          <input
            type="range"
            className={styles.slider}
            min="-100"
            max="100"
            value={filters.brightness}
            onChange={(e) => handleSliderChange('brightness', parseInt(e.target.value, 10))}
            onMouseUp={handleSliderEnd}
            onTouchEnd={handleSliderEnd}
          />
          <span className={styles.sliderValue}>{filters.brightness}%</span>
        </div>
      </div>

      <div className={styles.propertyGroup}>
        <label className={styles.label}>{t('design.filters.contrast', 'Contrast')}</label>
        <div className={styles.sliderWrapper}>
          <input
            type="range"
            className={styles.slider}
            min="-100"
            max="100"
            value={filters.contrast}
            onChange={(e) => handleSliderChange('contrast', parseInt(e.target.value, 10))}
            onMouseUp={handleSliderEnd}
            onTouchEnd={handleSliderEnd}
          />
          <span className={styles.sliderValue}>{filters.contrast}%</span>
        </div>
      </div>

      <div className={styles.propertyGroup}>
        <label className={styles.label}>{t('design.filters.blur', 'Blur')}</label>
        <div className={styles.sliderWrapper}>
          <input
            type="range"
            className={styles.slider}
            min="0"
            max="1"
            step="0.05"
            value={filters.blur}
            onChange={(e) => handleSliderChange('blur', parseFloat(e.target.value))}
            onMouseUp={handleSliderEnd}
            onTouchEnd={handleSliderEnd}
          />
          <span className={styles.sliderValue}>{Math.round(filters.blur * 100)}%</span>
        </div>
      </div>

      <div className={styles.propertyGroup}>
        <button
          className={styles.styleButton}
          onClick={handleResetFilters}
          style={{ width: '100%' }}
        >
          Reset All Filters
        </button>
      </div>
    </div>
  );
};

export default ImageFiltersPanel;
